import { useState, useEffect, createContext, useContext } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'

interface ProfileData {
  onboarding_completed: boolean
  quiz_completed: boolean
  category: string | null
}

interface ProfileContextType {
  profile: ProfileData | null
  loading: boolean
  refresh: () => void
}

const ProfileContext = createContext<ProfileContextType>({ profile: null, loading: true, refresh: () => {} })

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const [profile, setProfile] = useState<ProfileData | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (authLoading) return
    if (!user) {
      setProfile(null)
      setLoading(false)
      return
    }

    // Try cache first for instant render
    const cached = localStorage.getItem(`profile_${user.id}`)
    if (cached) {
      try { setProfile(JSON.parse(cached)) } catch { /* ignore */ }
    }

    Promise.resolve(
      supabase
        .from('profiles')
        .select('onboarding_completed, quiz_completed, category')
        .eq('user_id', user.id)
        .single()
    )
      .then(({ data }) => {
        const p: ProfileData = data
          ? {
              onboarding_completed: data.onboarding_completed ?? false,
              quiz_completed: data.quiz_completed ?? false,
              category: data.category ?? null,
            }
          : { onboarding_completed: false, quiz_completed: false, category: null }
        setProfile(p)
        localStorage.setItem(`profile_${user.id}`, JSON.stringify(p))
        setLoading(false)
      })
      .catch(() => {
        if (!profile) setProfile({ onboarding_completed: false, quiz_completed: false, category: null })
        setLoading(false)
      })
  }, [user, authLoading, refreshKey])

  const refresh = () => setRefreshKey(k => k + 1)

  return (
    <ProfileContext.Provider value={{ profile, loading: authLoading || loading, refresh }}>
      {children}
    </ProfileContext.Provider>
  )
}

export const useProfile = () => useContext(ProfileContext)
